/**
 * Rechtstexte nach den Entwürfen 5a–5c. numbet wird privat und ohne Einnahmen betrieben.
 * Die technischen Angaben (Speicherdauer, Protokolle, lokaler Speicher, Statistik) beschreiben,
 * was numbet und der Cluster tatsächlich tun – bei Änderungen dort hier nachziehen.
 * Text in [eckigen Klammern] würde als offener Platzhalter gelb hervorgehoben.
 * Das ist keine Rechtsberatung.
 */

export type Block = string | { list: string[] };

export interface LegalSection {
  title: string;
  body: Block[];
}

export interface LegalPage {
  path: string;
  title: string;
  asOf?: string;
  sections: LegalSection[];
}

const OPERATOR = "Thomas Hanke";
const ADDRESS = "Düsseldorfer Landstr. 145A, 47249 Duisburg";
export const CONTACT_EMAIL = "kontakt@numbet.games";
const AS_OF = "11. Oktober 2026";

export const imprint: LegalPage = {
  path: "/impressum",
  title: "Impressum",
  sections: [
    { title: "Anbieter", body: [OPERATOR, "Düsseldorfer Landstr. 145A", "47249 Duisburg", "numbet ist ein privates Projekt ohne Einnahmen."] },
    { title: "Kontakt", body: [`E-Mail: ${CONTACT_EMAIL}`] },
    { title: "Verantwortlich für den Inhalt", body: [`${OPERATOR}, Anschrift wie oben`] },
    {
      title: "Verbraucherstreitbeilegung",
      body: ["Ich bin weder bereit noch verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen."],
    },
  ],
};

export const terms: LegalPage = {
  path: "/nutzungsbedingungen",
  title: "Nutzungsbedingungen",
  asOf: AS_OF,
  sections: [
    {
      title: "Geltungsbereich",
      body: [
        "Diese Nutzungsbedingungen gelten für die Nutzung von numbet, einem Multiplayer-Webspiel für 2 bis 8 Personen, das Schätzfragen mit dem Ablauf von Texas Hold’em verbindet.",
        `Anbieter ist ${OPERATOR} (siehe Impressum). numbet ist kostenlos und ohne Anmeldung nutzbar.`,
      ],
    },
    {
      title: "Spiel ohne Geldwert",
      body: ["Gespielt wird mit Chips ohne Geldwert. Chips können nicht gekauft, verkauft oder ausgezahlt werden, und es gibt nichts zu gewinnen. numbet ist kein Glücksspiel."],
    },
    {
      title: "Räume und Nicknames",
      body: [
        "Du spielst in einem gemeinsamen Raum, den du über einen Link oder einen Code betrittst. Wähle einen Nicknamen, der niemanden beleidigt und keine Rechte Dritter verletzt.",
        "Nicknames sind für alle im Raum sichtbar. Gib dich nicht als eine andere Person aus und schreib keine Kontaktdaten in deinen Nicknamen. Wer Link oder Code kennt, kann beitreten – teile sie nur mit Leuten, mit denen du spielen willst.",
      ],
    },
    {
      title: "Fairplay",
      body: [
        "numbet lebt davon, dass niemand die Antwort kennt. Schlag sie nicht nach, sprich dich nicht mit anderen gegen den Rest des Tisches ab und spiel mit nur einem Platz pro Raum. numbet kann das nicht verhindern und setzt auf eure Fairness.",
      ],
    },
    {
      title: "Verfügbarkeit",
      body: [
        "numbet ist ein privates Projekt. Ich bemühe mich um einen stabilen Betrieb, kann aber keine ständige Verfügbarkeit zusagen. Wartungen und Störungen können laufende Partien unterbrechen, und ich kann numbet jederzeit ändern oder einstellen.",
        "Bei einem Verbindungsabbruch behältst du deinen Platz und kommst mit demselben Browser zurück. Solange du fehlst, wird nach Ablauf des Zeitlimits automatisch geschoben oder ausgestiegen. Nach drei Zeitüberschreitungen in Folge setzt du aus, bis du zurück bist. Räume werden sechs Stunden nach der letzten Aktivität gelöscht.",
      ],
    },
    {
      title: "Haftung",
      body: [
        "numbet ist kostenlos. Ich hafte deshalb nur für Vorsatz und grobe Fahrlässigkeit. Für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit hafte ich nach den gesetzlichen Vorschriften.",
        "Fragen, Hinweise und Antworten sind sorgfältig recherchiert. Für ihre Richtigkeit übernehme ich trotzdem keine Gewähr.",
      ],
    },
    {
      title: "Änderungen",
      body: ["Ich kann diese Nutzungsbedingungen ändern, etwa wenn neue Funktionen dazukommen. Es gilt die Fassung, die zu Beginn deiner Partie auf dieser Seite steht."],
    },
    {
      title: "Schlussbestimmungen",
      body: [
        "Es gilt das Recht der Bundesrepublik Deutschland. Zwingende Verbraucherschutzvorschriften des Staates, in dem du deinen gewöhnlichen Aufenthalt hast, bleiben unberührt.",
        "Ist eine Bestimmung unwirksam, bleiben die übrigen wirksam.",
      ],
    },
  ],
};

export const privacy: LegalPage = {
  path: "/datenschutz",
  title: "Datenschutz",
  asOf: AS_OF,
  sections: [
    {
      title: "Verantwortlicher",
      body: [`Verantwortlich für die Datenverarbeitung ist ${OPERATOR}, ${ADDRESS}, E-Mail: ${CONTACT_EMAIL}. numbet ist ein privates Projekt ohne Einnahmen.`],
    },
    {
      title: "Welche Daten ich verarbeite",
      body: [
        "Wenn du numbet nutzt, verarbeite ich folgende Daten:",
        {
          list: [
            "Nickname und gewählter Avatar",
            "Raumcode und Spieldaten der laufenden Partie (Schätzungen, Einsätze, Chipstände)",
            "Technische Verbindungsdaten wie IP-Adresse, Browserkennung und Zeitpunkt des Zugriffs",
            "Nur mit deiner Einwilligung: Angaben zur Nutzung für die Statistik (siehe „Nutzungsstatistik“)",
          ],
        },
      ],
    },
    {
      title: "Zwecke und Rechtsgrundlagen",
      body: [
        "Ich verarbeite diese Daten, um das Spiel durchzuführen (Art. 6 Abs. 1 lit. b DSGVO) und den Betrieb sicher zu halten, etwa um massenhafte Anfragen abzuwehren und Störungen zu untersuchen (Art. 6 Abs. 1 lit. f DSGVO). Die Nutzungsstatistik erhebe ich nur mit deiner Einwilligung (Art. 6 Abs. 1 lit. a DSGVO).",
      ],
    },
    {
      title: "Speicherdauer",
      body: [
        "Räume samt Nicknamen, Avataren und Spieldaten werden spätestens sechs Stunden nach der letzten Aktivität im Raum gelöscht. IP-Adressen zur Abwehr massenhafter Anfragen liegen nur für eine Minute im Arbeitsspeicher.",
        "Der vorgeschaltete Webserver protokolliert jeden Zugriff mit IP-Adresse, Zeitpunkt, aufgerufener Adresse und Browserkennung, damit sich Störungen und Angriffe untersuchen lassen. Diese Protokolle werden nicht ausgewertet und automatisch überschrieben, sobald sie eine feste Größe erreichen. numbet selbst protokolliert keine IP-Adressen.",
        "Die Datenbank wird täglich gesichert. Eine Sicherung kann Räume enthalten, die zu diesem Zeitpunkt offen waren, und wird nach spätestens acht Wochen gelöscht.",
      ],
    },
    {
      title: "Hosting und Empfänger",
      body: [
        "numbet und die Nutzungsstatistik (Matomo) laufen auf selbst betriebenen Servern, die ich bei der Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, gemietet habe. Die Server und die Datensicherungen stehen in Rechenzentren in Deutschland. Hetzner verarbeitet die Daten in meinem Auftrag (Art. 28 DSGVO).",
        "Darüber hinaus gebe ich keine Daten weiter. Schriften und alle anderen Bestandteile der Seite werden von numbet selbst ausgeliefert, ohne Google-Server oder andere Drittanbieter.",
      ],
    },
    {
      title: "Cookies und lokaler Speicher",
      body: [
        "Beim Spielen setzt numbet keine Cookies. Im lokalen Speicher deines Browsers liegen dein Nickname, dein Avatar, für jeden Raum ein zufälliges Sitzungs-Token, damit du nach einem Verbindungsabbruch an deinen Platz zurückkommst, und deine Entscheidung zur Nutzungsstatistik. Du kannst diese Daten jederzeit in deinem Browser löschen.",
        "Nur für die Anmeldung im Admin-Bereich wird ein technisch notwendiges Cookie gesetzt.",
      ],
    },
    {
      title: "Nutzungsstatistik",
      body: [
        "Nur wenn du im Hinweis „Darf numbet mitzählen?“ zustimmst, erfasse ich mit einer selbst betriebenen Matomo-Installation, wie numbet genutzt wird: aufgerufene Seiten, erstellte und betretene Räume, gestartete und beendete Partien sowie das Teilen von Einladungslinks. Raumcodes, Nicknames und Spielinhalte werden nicht erfasst.",
        "Dabei werden keine Cookies gesetzt und keine Wiedererkennungs-ID gebildet. Übermittelt werden Browserkennung und Spracheinstellung, deine IP-Adresse höchstens gekürzt. Die Einzeldaten werden nach 180 Tagen automatisch gelöscht. Danach bleiben nur zusammengefasste Berichte ohne Bezug zu einzelnen Besuchen.",
        "Rechtsgrundlage ist deine Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Du kannst sie jederzeit über „Statistik-Einstellungen“ unten auf jeder Seite widerrufen. Die bis dahin erfolgte Verarbeitung bleibt davon unberührt. Sendet dein Browser „Do Not Track“ oder „Global Privacy Control“, wird nichts erfasst.",
      ],
    },
    {
      title: "Deine Rechte",
      body: [
        "Du hast das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch (Art. 15 bis 21 DSGVO).",
        "Du kannst dich außerdem bei einer Datenschutz-Aufsichtsbehörde beschweren. Für numbet zuständig ist die Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen (LDI NRW).",
      ],
    },
    { title: "Kontakt", body: [`Für Fragen zum Datenschutz erreichst du mich unter ${CONTACT_EMAIL}.`] },
  ],
};

export const LEGAL_PAGES = [imprint, terms, privacy];
